/**
 * Robust TTS playback shared by the assistant workspace and the bubble.
 *
 * Why this exists: TTS audio arrives ASYNC over the socket, and iOS
 * only allows media playback that can be traced to a user gesture.
 * We keep ONE persistent HTMLAudioElement (unlocked by playing a
 * silent clip inside the tap) AND one shared AudioContext (unlocked
 * via resume() inside the tap). Playback tries the element first
 * (Blob URL — iOS ignores the ring/silent switch for media elements),
 * and falls back to Web Audio decode when the element refuses.
 * Every failure is logged loudly — silent playback failures have
 * burned us repeatedly.
 */

const SILENT_WAV =
  'data:audio/wav;base64,UklGRkQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YSAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==';

let sharedCtx: AudioContext | null = null;
let sharedEl: HTMLAudioElement | null = null;

function getCtx(): AudioContext | null {
  if (sharedCtx) return sharedCtx;
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    sharedCtx = new Ctor();
  } catch (err) {
    console.warn('[TTS] AudioContext unavailable:', err);
    return null;
  }
  return sharedCtx;
}

function getEl(): HTMLAudioElement {
  if (!sharedEl) sharedEl = new Audio();
  return sharedEl;
}

/**
 * Call SYNCHRONOUSLY inside a click/tap/keypress handler. Unlocks both
 * playback paths for audio that arrives later. Safe to call often.
 */
export function primeTtsPlayback(): void {
  try {
    const ctx = getCtx();
    if (ctx && ctx.state !== 'running') {
      void ctx.resume().catch(() => undefined);
    }
    const el = getEl();
    // Unmuted on purpose: iOS can refuse a later programmatic unmute
    // outside a gesture; the clip is silence anyway. Skip when real
    // audio is playing so we don't cut it off.
    if (el.paused) {
      el.src = SILENT_WAV;
      void el.play().catch(() => undefined);
    }
  } catch {
    /* priming is best-effort */
  }
}

/**
 * Natural pause (ms) to insert AFTER a spoken sentence, before the
 * next one starts. VITS reads with flat prosody and zero inter-sentence
 * gap, so back-to-back chunks feel rushed and robotic. A short,
 * punctuation-aware, slightly-jittered beat gives it breathing room:
 * a longer reaction after a question/exclamation, a breath after a
 * statement, more after a paragraph break. Kept subtle so playback
 * still feels responsive, not stilted.
 */
export function interSentencePauseMs(text?: string): number {
  const t = (text ?? '').trimEnd();
  // Tightened for Kokoro: it carries sentence-final prosody itself, so the
  // added inter-chunk gap only needs to be a light breath — the old
  // (VITS-era) values left an unnatural silence between sentences.
  let base: number;
  if (/[?!]["')\]]?$/.test(t)) base = 190;
  else if (/\.\.\.$|…$/.test(t)) base = 170;
  else if (/[.][")'\]]?$/.test(t)) base = 150;
  else if (/[:;,][")'\]]?$/.test(t)) base = 90;
  else base = 120;
  // Longer clauses earn a slightly bigger breath.
  if (t.length > 140) base += 30;
  // ±15% jitter so it doesn't tick like a metronome.
  const jitter = base * 0.15;
  return Math.round(base + (Math.random() * 2 - 1) * jitter);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export interface TtsPlaybackHandle {
  stop: () => void;
}

/**
 * Play one WAV chunk. `onDone(played)` fires exactly once — when
 * playback ends, fails on both paths, or is stopped; `played` says
 * whether any audio actually came out (used to surface an error toast
 * instead of failing silently).
 */
export function playWavBytes(
  bytes: Uint8Array,
  onDone: (played: boolean) => void,
): TtsPlaybackHandle {
  let settled = false;
  let stopped = false;
  let started = false;
  let webAudioSource: AudioBufferSourceNode | null = null;
  let objectUrl: string | null = null;
  const el = getEl();

  const finish = () => {
    if (settled) return;
    settled = true;
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
    onDone(started);
  };

  const tryWebAudio = (elementErr?: unknown) => {
    if (stopped || settled) {
      finish();
      return;
    }
    const ctx = getCtx();
    if (!ctx) {
      console.warn(
        '[TTS] element playback failed and Web Audio unavailable',
        elementErr,
      );
      finish();
      return;
    }
    if (ctx.state !== 'running') void ctx.resume().catch(() => undefined);
    // decodeAudioData detaches its input — hand it a copy.
    const copy = bytes.buffer.slice(
      bytes.byteOffset,
      bytes.byteOffset + bytes.byteLength,
    );
    ctx
      .decodeAudioData(copy as ArrayBuffer)
      .then((buf) => {
        if (stopped || settled) {
          finish();
          return;
        }
        try {
          const src = ctx.createBufferSource();
          src.buffer = buf;
          src.connect(ctx.destination);
          src.onended = finish;
          webAudioSource = src;
          src.start();
          started = true;
          console.warn('[TTS] element path failed; playing via Web Audio');
        } catch (e) {
          console.warn('[TTS] Web Audio start failed:', e);
          finish();
        }
      })
      .catch((e) => {
        console.warn('[TTS] Web Audio decode failed:', e);
        finish();
      });
  };

  try {
    objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
    el.onended = finish;
    el.onerror = () => {
      console.warn(
        '[TTS] element error:',
        el.error?.code,
        el.error?.message ?? '(no message)',
      );
      tryWebAudio(el.error);
    };
    el.src = objectUrl;
    el
      .play()
      .then(() => {
        started = true;
      })
      .catch((err: unknown) => {
        console.warn(
          '[TTS] element play() rejected:',
          (err as Error)?.name,
          (err as Error)?.message,
        );
        tryWebAudio(err);
      });
  } catch (err) {
    console.warn('[TTS] element setup failed:', err);
    tryWebAudio(err);
  }

  return {
    stop: () => {
      stopped = true;
      try {
        el.pause();
      } catch {
        /* noop */
      }
      try {
        webAudioSource?.stop();
      } catch {
        /* noop */
      }
      finish();
    },
  };
}
